'use client';

import React, { useState } from 'react';
import { Calendar, Building2, Filter, Download, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const dateRanges = [
  { id: 'dr-7d', label: 'Last 7 days' },
  { id: 'dr-30d', label: 'Last 30 days' },
  { id: 'dr-90d', label: 'Last 90 days' },
  { id: 'dr-ytd', label: 'Year to Date' },
  { id: 'dr-q1', label: 'Q1 2026' },
  { id: 'dr-q2', label: 'Q2 2026' },
];

const departments = [
  { id: 'dept-all', label: 'All Departments' },
  { id: 'dept-ops', label: 'Operations' },
  { id: 'dept-maint', label: 'Maintenance' },
  { id: 'dept-chem', label: 'Chemical Processing' },
  { id: 'dept-logistics', label: 'Logistics' },
  { id: 'dept-electrical', label: 'Electrical' },
  { id: 'dept-construction', label: 'Construction' },
];

const sites = [
  { id: 'site-all', label: 'All Sites' },
  { id: 'site-houston', label: 'Houston TX' },
  { id: 'site-baton', label: 'Baton Rouge LA' },
  { id: 'site-corpus', label: 'Corpus Christi TX' },
  { id: 'site-denver', label: 'Denver CO' },
];

export default function OverviewFilters() {
  const [dateRange, setDateRange] = useState('dr-q2');
  const [department, setDepartment] = useState('dept-all');
  const [site, setSite] = useState('site-all');
  const [showExport, setShowExport] = useState(false);

  const handleExport = (format: 'csv' | 'pdf') => {
    // TODO: Backend integration — POST /api/ehs/export with current filters
    toast.success(`Exporting ${format.toUpperCase()}...`, {
      description: 'Your report will be ready in a few seconds.',
    });
    setShowExport(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date Range */}
      <div className="flex items-center gap-1.5 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-3 py-2">
        <Calendar size={13} className="text-[hsl(var(--muted-foreground))]" />
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="bg-transparent text-sm font-500 text-[hsl(var(--foreground))] outline-none cursor-pointer"
        >
          {dateRanges.map((r) => (
            <option key={r.id} value={r.id} className="bg-[hsl(var(--card))]">{r.label}</option>
          ))}
        </select>
      </div>

      {/* Department */}
      <div className="flex items-center gap-1.5 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-3 py-2">
        <Building2 size={13} className="text-[hsl(var(--muted-foreground))]" />
        <select
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          className="bg-transparent text-sm font-500 text-[hsl(var(--foreground))] outline-none cursor-pointer"
        >
          {departments.map((d) => (
            <option key={d.id} value={d.id} className="bg-[hsl(var(--card))]">{d.label}</option>
          ))}
        </select>
      </div>

      {/* Site */}
      <div className="flex items-center gap-1.5 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-3 py-2">
        <Filter size={13} className="text-[hsl(var(--muted-foreground))]" />
        <select
          value={site}
          onChange={(e) => setSite(e.target.value)}
          className="bg-transparent text-sm font-500 text-[hsl(var(--foreground))] outline-none cursor-pointer"
        >
          {sites.map((s) => (
            <option key={s.id} value={s.id} className="bg-[hsl(var(--card))]">{s.label}</option>
          ))}
        </select>
      </div>

      {/* Export */}
      <div className="relative ml-auto">
        <button
          onClick={() => setShowExport((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm font-600 hover:opacity-90 transition-all duration-150 active:scale-95"
        >
          <Download size={13} />
          Export
          <ChevronDown size={12} className={`transition-transform duration-150 ${showExport ? 'rotate-180' : ''}`} />
        </button>
        {showExport && (
          <div className="absolute right-0 top-full mt-1 w-36 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg shadow-lg z-20 overflow-hidden animate-fade-in">
            <button
              onClick={() => handleExport('csv')}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--muted))] transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={() => handleExport('pdf')}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[hsl(var(--muted))] transition-colors"
            >
              Export PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
}